export function isGenericMvProcedure(procedure) {
  if (!procedure || procedure.archived) return false;
  const isMultiVisit = Boolean(procedure.multi_visit) || Number(procedure.visit_count || 1) > 1;
  const category = String(procedure.category || "").toLowerCase();
  if (!isMultiVisit || procedure.ortho_visit_type || category.includes("ortho")) return false;
  return String(procedure.multi_tooth_kind || "").toLowerCase() !== "bridge";
}

// Operational MV cases use treatment-plan tables as their financial and visit
// ledger, but they are not proposals. Keep them out of every treatment-plan
// authoring surface while leaving the underlying rows available to Today,
// Checkout, reporting, and Treatment Plan Center.
export function isOperationalMvPlan(plan) {
  if (!plan) return false;
  if (plan.is_informal === true) return true;
  if (String(plan.creation_source || "").toLowerCase() === "mv_historical_import") return true;
  return /^MV-/i.test(String(plan.plan_code || "").trim());
}

export function treatmentPlanAuthoringPlans(plans) {
  return (Array.isArray(plans) ? plans : []).filter((plan) => !isOperationalMvPlan(plan));
}

export function showOperationalMvInChartMode(chartMode) {
  return chartMode === "today";
}

export function isDentureMvProcedure(procedure) {
  return ["partial_denture", "full_denture"].includes(
    String(procedure?.multi_tooth_kind || "").toLowerCase(),
  );
}

export function isPartialDentureProcedure(procedure) {
  return String(procedure?.multi_tooth_kind || "").toLowerCase() === "partial_denture";
}

const CLOSED_MV_ITEM_STATUSES = new Set(["completed", "cancelled", "declined"]);

export function activeMvItems(bundle) {
  if (!bundle) return [];
  return (Array.isArray(bundle.procedures) ? bundle.procedures : []).filter((item) => (
    item?.is_multi_visit !== false
    && !CLOSED_MV_ITEM_STATUSES.has(String(item?.status || "").toLowerCase())
  ));
}

// Calendar needs two related but different answers:
//   1. Which MV items are still active for this patient?
//   2. Which plan items may receive a new appointment right now?
// list_patient_unbooked_plan_work answers only #2. An empty result can mean
// the active MV already has an appointment, so it must never erase #1 from
// get_patient_bundle. Merge both read models and keep bookability explicit.
export function calendarPlanWork(bundle, bookableWork) {
  const bookableRows = Array.isArray(bookableWork) ? bookableWork : [];
  const bookableByItemId = new Map(
    bookableRows
      .filter((item) => item?.item_id)
      .map((item) => [item.item_id, item]),
  );
  const activeRows = activeMvItems(bundle).map((item) => {
    const bookable = bookableByItemId.get(item.item_id);
    if (bookable) {
      return { ...item, ...bookable, booking_available: true };
    }
    return {
      ...item,
      plan_id: bundle?.plan_id || null,
      plan_code: bundle?.mv_code || null,
      plan_kind: "mv",
      balance_centavos: bundle?.balance_centavos ?? null,
      booking_available: false,
    };
  });
  const activeIds = new Set(activeRows.map((item) => item.item_id));
  const otherBookableRows = bookableRows
    .filter((item) => !activeIds.has(item?.item_id))
    .map((item) => ({ ...item, booking_available: true }));
  return [...activeRows, ...otherBookableRows];
}

// Calendar books an operational MV by its plan identity, not by whichever
// procedure row the receptionist happened to click first. Formal Treatment
// Plans remain item-by-item because each accepted item has its own visit path.
export function calendarBookingRows(work) {
  const rows = [];
  const mvGroups = new Map();

  for (const item of (Array.isArray(work) ? work : [])) {
    if (item?.plan_kind !== "mv" || !item?.plan_id) {
      rows.push({
        row_kind: "item",
        row_id: item?.item_id || `item-${rows.length}`,
        booking_available: item?.booking_available === true,
        items: [item],
        ...item,
      });
      continue;
    }

    let group = mvGroups.get(item.plan_id);
    if (!group) {
      group = {
        row_kind: "mv_group",
        row_id: `mv-${item.plan_id}`,
        plan_id: item.plan_id,
        plan_code: item.plan_code,
        plan_kind: "mv",
        balance_centavos: item.balance_centavos,
        booking_available: true,
        items: [],
      };
      mvGroups.set(item.plan_id, group);
      rows.push(group);
    }

    group.items.push(item);
    group.booking_available = group.booking_available && item?.booking_available === true;
    if (group.balance_centavos == null && item.balance_centavos != null) {
      group.balance_centavos = item.balance_centavos;
    }
  }

  return rows;
}

export function activeMvItemsForProcedure(bundle, procedure) {
  if (!bundle || !procedure?.id || !isGenericMvProcedure(procedure)) return [];
  return activeMvItems(bundle).filter((item) => (
    item?.procedure_id === procedure.id
  ));
}

export function mvItemScopeLabel(item) {
  const arch = String(item?.arch || "").toLowerCase();
  if (arch === "upper") return "Upper arch";
  if (arch === "lower") return "Lower arch";
  if (item?.tooth_number) return `Tooth #${item.tooth_number}`;
  return "Scope confirmed in Chairside";
}
